import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Capabilities", href: "#capabilities" },
  { label: "Process", href: "#showcase" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-700 ${
        scrolled
          ? "bg-[#05070A]/80 backdrop-blur-2xl border-b border-white/[0.04]"
          : "bg-[#05070A]/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <button
          data-testid="navbar-logo"
          onClick={() => scrollTo("#hero")}
          className="font-heading text-sm tracking-[0.35em] uppercase hover:opacity-80 transition-opacity duration-700 inline-flex"
        >
          <span className="text-white/90">D</span>
          <span style={{ backgroundImage: "linear-gradient(135deg, #C49B3C, #F5D77A, #A67C28)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>E</span>
          <span style={{ backgroundImage: "linear-gradient(135deg, #00E5FF, #7A5CFF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>V</span>
          <span style={{ backgroundImage: "linear-gradient(135deg, #7A5CFF, #FF2D9A)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>A</span>
          <span className="text-white/90">MIND&nbsp;AI</span>
        </button>

        <div className="hidden lg:flex items-center gap-12">
          {links.map((link) => (
            <button
              key={link.label}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={() => scrollTo(link.href)}
              className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/60 hover:text-[#00E5FF] transition-colors duration-500"
            >
              {link.label}
            </button>
          ))}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-white hover:text-[#00E5FF] transition-colors duration-300"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden absolute top-20 left-4 right-4 z-40 rounded-xl overflow-hidden"
            style={{
              background: "rgba(11, 15, 20, 0.45)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
            }}
          >
            <div className="py-4 px-5">
              {links.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  onClick={() => scrollTo(link.href)}
                  className="block w-full font-mono text-[11px] tracking-[0.2em] uppercase text-white/70 hover:text-[#00E5FF] transition-colors duration-400 text-left py-3 border-b border-white/[0.05] last:border-b-0"
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
